/**
 * A small, hand-rolled markdown-to-JSX renderer -- not a dependency, for
 * the same reason ValuationBandChart.tsx hand-rolls its own SVG instead of
 * fighting react-financial-charts for a shape it has no primitive for:
 * this only needs to render a handful of specific internal research docs
 * readably for a skeptical public reader, not arbitrary GFM. Headings,
 * bold, italic, inline code, bullet lists, blockquotes, paragraphs, and
 * GFM-style pipe tables (parsed into real <table> markup, with column
 * alignment honored) all render properly. No new npm dependency.
 */
// React text nodes render strings verbatim -- an HTML entity like "&nbsp;"
// in the source markdown (used as a spacer in one of these docs' tables)
// would otherwise show up as the LITERAL TEXT "&nbsp;" on the page, since
// JSX never runs an HTML parser over a text child. Decode the handful of
// entities that actually appear in these specific docs.
function decodeEntities(text: string): string {
  return text
    .replace(/&nbsp;/g, " ")
    .replace(/&mdash;/g, "—")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"');
}

function renderInline(rawText: string, keyBase: string): React.ReactNode[] {
  const text = decodeEntities(rawText);
  const parts: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith("**")) parts.push(<b key={`${keyBase}-${i++}`}>{token.slice(2, -2)}</b>);
    else if (token.startsWith("`")) parts.push(<code key={`${keyBase}-${i++}`} className="mono sm-inline-code">{token.slice(1, -1)}</code>);
    else parts.push(<i key={`${keyBase}-${i++}`}>{token.slice(1, -1)}</i>);
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

type Align = "l" | "c" | "r" | null;

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  // Split on unescaped pipes only (a literal pipe can appear as \|, e.g.
  // inside inline code spans in these specific docs' tables).
  return s.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c));
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(":");
  const right = cell.endsWith(":");
  if (left && right) return "c";
  if (right) return "r";
  if (left) return "l";
  return null;
}

function Table({ rows, keyBase }: { rows: string[]; keyBase: string }) {
  if (rows.length === 0) return null;
  const header = splitTableRow(rows[0]);
  const sepRow = rows.length > 1 ? splitTableRow(rows[1]) : [];
  const hasSeparator = isSeparatorRow(sepRow);
  const aligns = hasSeparator ? sepRow.map(alignOf) : header.map(() => null as Align);
  const bodyRows = (hasSeparator ? rows.slice(2) : rows.slice(1)).map(splitTableRow);

  const alignStyle = (a: Align): React.CSSProperties | undefined =>
    a === "r" ? { textAlign: "right" } : a === "c" ? { textAlign: "center" } : undefined;

  return (
    <div className="sm-table-wrap">
      <table className="sm-table">
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} style={alignStyle(aligns[i])}>{renderInline(h, `${keyBase}-th-${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} style={alignStyle(aligns[ci])}>{renderInline(cell, `${keyBase}-td-${ri}-${ci}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SimpleMarkdown({ markdown }: { markdown: string }) {
  const lines = markdown.split("\n");
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  let tableBuf: string[] = [];
  let key = 0;

  function flushPara() {
    if (para.length) {
      blocks.push(<p key={key++}>{renderInline(para.join(" "), `p${key}`)}</p>);
      para = [];
    }
  }
  function flushTable() {
    if (tableBuf.length) {
      blocks.push(<Table key={key++} rows={tableBuf} keyBase={`t${key}`} />);
      tableBuf = [];
    }
  }

  for (let idx = 0; idx < lines.length; idx++) {
    const line = lines[idx].trimEnd();

    // Fenced code block (```...```) -- rendered raw, no inline parsing
    // inside, so formula-shaped content (e.g. "SUE_e = (EPS_T - EPS_{T-4})
    // / sigma_prior") never gets mangled by the bold/italic/code regex.
    // Found missing by an actual pixel-check: without this, a fence fell
    // through to plain-paragraph handling and rendered broken.
    if (/^```/.test(line.trim())) {
      flushPara();
      flushTable();
      const codeLines: string[] = [];
      idx++;
      while (idx < lines.length && !/^```/.test(lines[idx].trim())) {
        codeLines.push(lines[idx]);
        idx++;
      }
      blocks.push(<pre key={key++} className="mono sm-codeblock">{codeLines.join("\n")}</pre>);
      continue;
    }

    const isTableRow = /^\s*\|/.test(line);
    if (isTableRow) {
      flushPara();
      tableBuf.push(line);
      continue;
    }
    flushTable();

    if (line.trim() === "" || line.trim() === "---") {
      flushPara();
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      const level = h[1].length;
      const content = renderInline(h[2], `h${key}`);
      if (level === 1) blocks.push(<h2 key={key++}>{content}</h2>);
      else if (level === 2) blocks.push(<h3 key={key++}>{content}</h3>);
      else blocks.push(<h4 key={key++}>{content}</h4>);
      continue;
    }
    if (/^>\s?/.test(line)) {
      flushPara();
      blocks.push(<blockquote key={key++} className="sm-quote">{renderInline(line.replace(/^>\s?/, ""), `q${key}`)}</blockquote>);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      blocks.push(<li key={key++} className="sm-li">{renderInline(line.replace(/^[-*]\s+/, ""), `l${key}`)}</li>);
      continue;
    }
    para.push(line.trim());
  }
  flushPara();
  flushTable();

  return <div className="sm-doc">{blocks}</div>;
}
